/**
 * Recipe - Defines crafting recipes
 */

import { ItemType } from '../types/enums';

export interface MaterialRequirement {
  itemId: string;
  quantity: number;
}

export interface RecipeResult {
  itemId: string;
  quantity: number;
}

export interface Recipe {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly ingredients: MaterialRequirement[];
  readonly result: RecipeResult;
  readonly craftTime: number;
  readonly requiredStation: string | null;
  readonly category: ItemType;
}

// Default recipes
export const RECIPES: Recipe[] = [
  // Jackdrill - FREE FOR TESTING (creates Hell Holes)
  {
    id: 'jackdrill',
    name: 'Jackdrill',
    description: 'A powerful drill that can pierce through to other dimensions.',
    ingredients: [], // Free for testing!
    result: { itemId: 'jackdrill', quantity: 1 },
    craftTime: 1,
    requiredStation: null,
    category: ItemType.Tool
  },
  // Basic tools (by hand)
  {
    id: 'torch',
    name: 'Torch',
    description: 'A handheld light source.',
    ingredients: [
      { itemId: 'wood', quantity: 2 },
      { itemId: 'fiber', quantity: 1 }
    ],
    result: { itemId: 'torch', quantity: 1 },
    craftTime: 1,
    requiredStation: null,
    category: ItemType.Tool
  },
  {
    id: 'axe_wood',
    name: 'Wooden Axe',
    description: 'A basic axe for chopping trees.',
    ingredients: [
      { itemId: 'wood', quantity: 5 },
      { itemId: 'stone', quantity: 2 }
    ],
    result: { itemId: 'axe_wood', quantity: 1 },
    craftTime: 2,
    requiredStation: null,
    category: ItemType.Tool
  },
  {
    id: 'pickaxe_wood',
    name: 'Wooden Pickaxe',
    description: 'A basic pickaxe for mining stone.',
    ingredients: [
      { itemId: 'wood', quantity: 5 },
      { itemId: 'stone', quantity: 3 }
    ],
    result: { itemId: 'pickaxe_wood', quantity: 1 },
    craftTime: 2,
    requiredStation: null,
    category: ItemType.Tool
  },
  {
    id: 'sword_wood',
    name: 'Wooden Sword',
    description: 'A basic wooden sword.',
    ingredients: [
      { itemId: 'wood', quantity: 8 }
    ],
    result: { itemId: 'sword_wood', quantity: 1 },
    craftTime: 2,
    requiredStation: null,
    category: ItemType.Weapon
  },
  {
    id: 'spear_wood',
    name: 'Wooden Spear',
    description: 'A simple spear with long reach.',
    ingredients: [
      { itemId: 'wood', quantity: 6 },
      { itemId: 'stone', quantity: 1 }
    ],
    result: { itemId: 'spear_wood', quantity: 1 },
    craftTime: 2,
    requiredStation: null,
    category: ItemType.Weapon
  },
  {
    id: 'sickle_wood',
    name: 'Wooden Sickle',
    description: 'A basic sickle for harvesting plants.',
    ingredients: [
      { itemId: 'wood', quantity: 4 },
      { itemId: 'stone', quantity: 2 }
    ],
    result: { itemId: 'sickle_wood', quantity: 1 },
    craftTime: 2,
    requiredStation: null,
    category: ItemType.Tool
  },

  // Advanced tools (workbench)
  {
    id: 'axe_stone',
    name: 'Stone Axe',
    description: 'An improved axe.',
    ingredients: [
      { itemId: 'wood', quantity: 3 },
      { itemId: 'stone', quantity: 10 }
    ],
    result: { itemId: 'axe_stone', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Tool
  },
  {
    id: 'pickaxe_stone',
    name: 'Stone Pickaxe',
    description: 'An improved pickaxe.',
    ingredients: [
      { itemId: 'wood', quantity: 3 },
      { itemId: 'stone', quantity: 10 }
    ],
    result: { itemId: 'pickaxe_stone', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Tool
  },
  {
    id: 'sickle_stone',
    name: 'Stone Sickle',
    description: 'An improved sickle for harvesting.',
    ingredients: [
      { itemId: 'wood', quantity: 2 },
      { itemId: 'stone', quantity: 8 }
    ],
    result: { itemId: 'sickle_stone', quantity: 1 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Tool
  },
  {
    id: 'sword_stone',
    name: 'Stone Sword',
    description: 'A stone-bladed sword.',
    ingredients: [
      { itemId: 'wood', quantity: 3 },
      { itemId: 'stone', quantity: 12 }
    ],
    result: { itemId: 'sword_stone', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'spear_stone',
    name: 'Stone Spear',
    description: 'A stone-tipped spear with deadly reach.',
    ingredients: [
      { itemId: 'wood', quantity: 4 },
      { itemId: 'stone', quantity: 8 }
    ],
    result: { itemId: 'spear_stone', quantity: 1 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },

  // ==========================================
  // MONSTER DROP RECIPES (workbench required)
  // ==========================================

  // Bone weapons
  {
    id: 'bone_knife',
    name: 'Bone Knife',
    description: 'A fast dagger carved from bone.',
    ingredients: [
      { itemId: 'bone', quantity: 3 },
      { itemId: 'stone', quantity: 1 }
    ],
    result: { itemId: 'bone_knife', quantity: 1 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'bone_spear',
    name: 'Bone Spear',
    description: 'A spear tipped with sharpened bone.',
    ingredients: [
      { itemId: 'bone', quantity: 4 },
      { itemId: 'wood', quantity: 2 }
    ],
    result: { itemId: 'bone_spear', quantity: 1 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },

  // Iron tier tools and weapons
  {
    id: 'axe_iron',
    name: 'Iron Axe',
    description: 'A sturdy iron axe.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 3 },
      { itemId: 'wood', quantity: 2 }
    ],
    result: { itemId: 'axe_iron', quantity: 1 },
    craftTime: 5,
    requiredStation: 'workbench',
    category: ItemType.Tool
  },
  {
    id: 'pickaxe_iron',
    name: 'Iron Pickaxe',
    description: 'A sturdy iron pickaxe.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 3 },
      { itemId: 'wood', quantity: 2 }
    ],
    result: { itemId: 'pickaxe_iron', quantity: 1 },
    craftTime: 5,
    requiredStation: 'workbench',
    category: ItemType.Tool
  },
  {
    id: 'sword_iron',
    name: 'Iron Sword',
    description: 'A proper iron sword.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 4 },
      { itemId: 'wood', quantity: 1 }
    ],
    result: { itemId: 'sword_iron', quantity: 1 },
    craftTime: 5,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },

  // Fiber crafts (can craft by hand)
  {
    id: 'bandage',
    name: 'Bandage',
    description: 'Cloth bandages for treating wounds.',
    ingredients: [
      { itemId: 'fiber', quantity: 3 }
    ],
    result: { itemId: 'bandage', quantity: 2 },
    craftTime: 2,
    requiredStation: null,
    category: ItemType.Consumable
  },

  // Wolf pelt crafts
  {
    id: 'fur_cloak',
    name: 'Fur Cloak',
    description: 'A warm cloak from wolf pelts.',
    ingredients: [
      { itemId: 'wolf_pelt', quantity: 2 },
      { itemId: 'fiber', quantity: 3 }
    ],
    result: { itemId: 'fur_cloak', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Consumable
  },

  // Golem core - end game weapon
  {
    id: 'golem_sword',
    name: 'Golem Sword',
    description: 'A powerful sword infused with golem energy.',
    ingredients: [
      { itemId: 'golem_core', quantity: 1 },
      { itemId: 'iron_ore', quantity: 5 },
      { itemId: 'stone', quantity: 10 }
    ],
    result: { itemId: 'golem_sword', quantity: 1 },
    craftTime: 8,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },

  // Spider crafts
  {
    id: 'antidote',
    name: 'Antidote',
    description: 'Made from spider venom. Restores vitality.',
    ingredients: [
      { itemId: 'spider_eye', quantity: 2 },
      { itemId: 'fiber', quantity: 1 }
    ],
    result: { itemId: 'antidote', quantity: 1 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Consumable
  },

  // Ghost crafts
  {
    id: 'spirit_essence',
    name: 'Spirit Essence',
    description: 'Concentrated ghost energy.',
    ingredients: [
      { itemId: 'ectoplasm', quantity: 2 }
    ],
    result: { itemId: 'spirit_essence', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Consumable
  },

  // Health potion craft
  {
    id: 'health_potion_craft',
    name: 'Health Potion',
    description: 'Brew a health potion.',
    ingredients: [
      { itemId: 'spider_eye', quantity: 1 },
      { itemId: 'apple', quantity: 3 }
    ],
    result: { itemId: 'health_potion', quantity: 1 },
    craftTime: 5,
    requiredStation: 'workbench',
    category: ItemType.Consumable
  },

  // ==========================================
  // REGULAR TIER - Cloth Armor (by hand)
  // ==========================================
  {
    id: 'cloth_hood',
    name: 'Cloth Hood',
    description: 'A simple hood woven from plant fibers.',
    ingredients: [
      { itemId: 'fiber', quantity: 5 }
    ],
    result: { itemId: 'cloth_hood', quantity: 1 },
    craftTime: 2,
    requiredStation: null,
    category: ItemType.Armor
  },
  {
    id: 'cloth_shirt',
    name: 'Cloth Shirt',
    description: 'A basic shirt offering minimal protection.',
    ingredients: [
      { itemId: 'fiber', quantity: 8 }
    ],
    result: { itemId: 'cloth_shirt', quantity: 1 },
    craftTime: 3,
    requiredStation: null,
    category: ItemType.Armor
  },
  {
    id: 'cloth_pants',
    name: 'Cloth Pants',
    description: 'Simple cloth leg coverings.',
    ingredients: [
      { itemId: 'fiber', quantity: 6 }
    ],
    result: { itemId: 'cloth_pants', quantity: 1 },
    craftTime: 2,
    requiredStation: null,
    category: ItemType.Armor
  },
  {
    id: 'cloth_sandals',
    name: 'Cloth Sandals',
    description: 'Light sandals for nimble movement.',
    ingredients: [
      { itemId: 'fiber', quantity: 4 }
    ],
    result: { itemId: 'cloth_sandals', quantity: 1 },
    craftTime: 2,
    requiredStation: null,
    category: ItemType.Armor
  },
  {
    id: 'cloth_wraps',
    name: 'Cloth Wraps',
    description: 'Cloth wrappings for the hands.',
    ingredients: [
      { itemId: 'fiber', quantity: 3 }
    ],
    result: { itemId: 'cloth_wraps', quantity: 1 },
    craftTime: 1,
    requiredStation: null,
    category: ItemType.Armor
  },

  // ==========================================
  // REGULAR TIER - Leather Armor (workbench)
  // ==========================================
  {
    id: 'leather_cap',
    name: 'Leather Cap',
    description: 'A simple leather cap.',
    ingredients: [
      { itemId: 'fiber', quantity: 5 },
      { itemId: 'wood', quantity: 2 }
    ],
    result: { itemId: 'leather_cap', quantity: 1 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'leather_tunic',
    name: 'Leather Tunic',
    description: 'A sturdy leather tunic.',
    ingredients: [
      { itemId: 'fiber', quantity: 10 },
      { itemId: 'wood', quantity: 3 }
    ],
    result: { itemId: 'leather_tunic', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'leather_pants',
    name: 'Leather Pants',
    description: 'Tough leather leg protection.',
    ingredients: [
      { itemId: 'fiber', quantity: 8 },
      { itemId: 'wood', quantity: 2 }
    ],
    result: { itemId: 'leather_pants', quantity: 1 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'leather_boots',
    name: 'Leather Boots',
    description: 'Comfortable leather boots.',
    ingredients: [
      { itemId: 'fiber', quantity: 6 },
      { itemId: 'wood', quantity: 1 }
    ],
    result: { itemId: 'leather_boots', quantity: 1 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'leather_gloves',
    name: 'Leather Gloves',
    description: 'Protective leather gloves.',
    ingredients: [
      { itemId: 'fiber', quantity: 4 },
      { itemId: 'wood', quantity: 1 }
    ],
    result: { itemId: 'leather_gloves', quantity: 1 },
    craftTime: 2,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },

  // ==========================================
  // MAGIC TIER - Iron Armor (workbench)
  // ==========================================
  {
    id: 'iron_helm',
    name: 'Iron Helm',
    description: 'A sturdy iron helmet.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 4 },
      { itemId: 'fiber', quantity: 2 }
    ],
    result: { itemId: 'iron_helm', quantity: 1 },
    craftTime: 5,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'iron_chestplate',
    name: 'Iron Chestplate',
    description: 'Heavy iron chest protection.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 8 },
      { itemId: 'fiber', quantity: 4 }
    ],
    result: { itemId: 'iron_chestplate', quantity: 1 },
    craftTime: 8,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'iron_greaves',
    name: 'Iron Greaves',
    description: 'Protective iron leg armor.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 6 },
      { itemId: 'fiber', quantity: 3 }
    ],
    result: { itemId: 'iron_greaves', quantity: 1 },
    craftTime: 6,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'iron_boots',
    name: 'Iron Boots',
    description: 'Heavy iron boots.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 4 },
      { itemId: 'fiber', quantity: 2 }
    ],
    result: { itemId: 'iron_boots', quantity: 1 },
    craftTime: 5,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },

  // ==========================================
  // MAGIC TIER - Bone Armor (workbench)
  // ==========================================
  {
    id: 'bone_helm',
    name: 'Bone Helm',
    description: 'A menacing helmet crafted from bones.',
    ingredients: [
      { itemId: 'bone', quantity: 6 },
      { itemId: 'fiber', quantity: 3 }
    ],
    result: { itemId: 'bone_helm', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'bone_armor',
    name: 'Bone Armor',
    description: 'Ribcage armor held together by sinew.',
    ingredients: [
      { itemId: 'bone', quantity: 10 },
      { itemId: 'fiber', quantity: 5 }
    ],
    result: { itemId: 'bone_armor', quantity: 1 },
    craftTime: 6,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'bone_greaves',
    name: 'Bone Greaves',
    description: 'Leg guards made from large bones.',
    ingredients: [
      { itemId: 'bone', quantity: 8 },
      { itemId: 'fiber', quantity: 4 }
    ],
    result: { itemId: 'bone_greaves', quantity: 1 },
    craftTime: 5,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'bone_boots',
    name: 'Bone Boots',
    description: 'Boots reinforced with bone plates.',
    ingredients: [
      { itemId: 'bone', quantity: 5 },
      { itemId: 'fiber', quantity: 2 }
    ],
    result: { itemId: 'bone_boots', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },

  // ==========================================
  // MAGIC TIER - Wolf Armor (workbench)
  // ==========================================
  {
    id: 'wolf_hood',
    name: 'Wolf Hood',
    description: 'A hood made from wolf hide.',
    ingredients: [
      { itemId: 'wolf_pelt', quantity: 1 },
      { itemId: 'fiber', quantity: 3 }
    ],
    result: { itemId: 'wolf_hood', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'wolf_pelt_cloak',
    name: 'Wolf Pelt Cloak',
    description: 'A warm cloak made from wolf pelts.',
    ingredients: [
      { itemId: 'wolf_pelt', quantity: 3 },
      { itemId: 'fiber', quantity: 5 }
    ],
    result: { itemId: 'wolf_pelt_cloak', quantity: 1 },
    craftTime: 6,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'wolf_pants',
    name: 'Wolf Leggings',
    description: 'Warm leggings from wolf pelts.',
    ingredients: [
      { itemId: 'wolf_pelt', quantity: 2 },
      { itemId: 'fiber', quantity: 4 }
    ],
    result: { itemId: 'wolf_pants', quantity: 1 },
    craftTime: 5,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'wolf_boots',
    name: 'Wolf Boots',
    description: 'Soft boots for silent movement.',
    ingredients: [
      { itemId: 'wolf_pelt', quantity: 1 },
      { itemId: 'fiber', quantity: 3 }
    ],
    result: { itemId: 'wolf_boots', quantity: 1 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'wolf_gloves',
    name: 'Wolf Gloves',
    description: 'Clawed gloves made from wolf hide.',
    ingredients: [
      { itemId: 'wolf_pelt', quantity: 1 },
      { itemId: 'fiber', quantity: 2 }
    ],
    result: { itemId: 'wolf_gloves', quantity: 1 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },

  // ==========================================
  // EPIC TIER - Golem Armor (workbench)
  // ==========================================
  {
    id: 'golem_helm',
    name: 'Golem Helm',
    description: 'A helmet of living stone.',
    ingredients: [
      { itemId: 'golem_core', quantity: 1 },
      { itemId: 'iron_ore', quantity: 4 },
      { itemId: 'stone', quantity: 15 }
    ],
    result: { itemId: 'golem_helm', quantity: 1 },
    craftTime: 10,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'golem_chestplate',
    name: 'Golem Chestplate',
    description: 'Massive stone armor pulsing with power.',
    ingredients: [
      { itemId: 'golem_core', quantity: 2 },
      { itemId: 'iron_ore', quantity: 6 },
      { itemId: 'stone', quantity: 25 }
    ],
    result: { itemId: 'golem_chestplate', quantity: 1 },
    craftTime: 15,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'golem_greaves',
    name: 'Golem Greaves',
    description: 'Leg armor of enchanted stone.',
    ingredients: [
      { itemId: 'golem_core', quantity: 1 },
      { itemId: 'iron_ore', quantity: 5 },
      { itemId: 'stone', quantity: 20 }
    ],
    result: { itemId: 'golem_greaves', quantity: 1 },
    craftTime: 12,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'golem_gauntlets',
    name: 'Golem Gauntlets',
    description: 'Gauntlets forged from golem cores.',
    ingredients: [
      { itemId: 'golem_core', quantity: 1 },
      { itemId: 'iron_ore', quantity: 3 },
      { itemId: 'stone', quantity: 10 }
    ],
    result: { itemId: 'golem_gauntlets', quantity: 1 },
    craftTime: 8,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'golem_boots',
    name: 'Golem Boots',
    description: 'Heavy boots that shake the ground.',
    ingredients: [
      { itemId: 'golem_core', quantity: 1 },
      { itemId: 'iron_ore', quantity: 4 },
      { itemId: 'stone', quantity: 15 }
    ],
    result: { itemId: 'golem_boots', quantity: 1 },
    craftTime: 10,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },

  // ==========================================
  // EPIC TIER - Demon Armor (workbench)
  // ==========================================
  {
    id: 'demon_crown',
    name: 'Demon Crown',
    description: 'A sinister crown pulsing with dark energy.',
    ingredients: [
      { itemId: 'demon_heart', quantity: 1 },
      { itemId: 'iron_ore', quantity: 4 },
      { itemId: 'bone', quantity: 5 }
    ],
    result: { itemId: 'demon_crown', quantity: 1 },
    craftTime: 12,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'demon_chestplate',
    name: 'Demon Chestplate',
    description: 'Armor forged in hellfire.',
    ingredients: [
      { itemId: 'demon_heart', quantity: 2 },
      { itemId: 'iron_ore', quantity: 6 },
      { itemId: 'bone', quantity: 8 }
    ],
    result: { itemId: 'demon_chestplate', quantity: 1 },
    craftTime: 15,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'demon_greaves',
    name: 'Demon Greaves',
    description: 'Leg armor burning with infernal energy.',
    ingredients: [
      { itemId: 'demon_heart', quantity: 1 },
      { itemId: 'iron_ore', quantity: 5 },
      { itemId: 'bone', quantity: 6 }
    ],
    result: { itemId: 'demon_greaves', quantity: 1 },
    craftTime: 12,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'demon_boots',
    name: 'Demon Boots',
    description: 'Boots that leave smoldering footprints.',
    ingredients: [
      { itemId: 'demon_heart', quantity: 1 },
      { itemId: 'iron_ore', quantity: 3 },
      { itemId: 'bone', quantity: 4 }
    ],
    result: { itemId: 'demon_boots', quantity: 1 },
    craftTime: 10,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'demon_gloves',
    name: 'Demon Gauntlets',
    description: 'Gauntlets wreathed in dark flames.',
    ingredients: [
      { itemId: 'demon_heart', quantity: 1 },
      { itemId: 'iron_ore', quantity: 3 },
      { itemId: 'bone', quantity: 4 }
    ],
    result: { itemId: 'demon_gloves', quantity: 1 },
    craftTime: 10,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },

  // ==========================================
  // EPIC TIER - Spectral Armor (workbench)
  // ==========================================
  {
    id: 'spectral_hood',
    name: 'Spectral Hood',
    description: 'A hood shimmering with ghostly energy.',
    ingredients: [
      { itemId: 'ectoplasm', quantity: 3 },
      { itemId: 'fiber', quantity: 5 }
    ],
    result: { itemId: 'spectral_hood', quantity: 1 },
    craftTime: 8,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'spectral_robe',
    name: 'Spectral Robe',
    description: 'A flowing robe that phases through attacks.',
    ingredients: [
      { itemId: 'ectoplasm', quantity: 5 },
      { itemId: 'fiber', quantity: 8 }
    ],
    result: { itemId: 'spectral_robe', quantity: 1 },
    craftTime: 12,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'spectral_leggings',
    name: 'Spectral Leggings',
    description: 'Ghostly leg wrappings that enhance agility.',
    ingredients: [
      { itemId: 'ectoplasm', quantity: 4 },
      { itemId: 'fiber', quantity: 6 }
    ],
    result: { itemId: 'spectral_leggings', quantity: 1 },
    craftTime: 10,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'spectral_boots',
    name: 'Spectral Boots',
    description: 'Boots that barely touch the ground.',
    ingredients: [
      { itemId: 'ectoplasm', quantity: 3 },
      { itemId: 'fiber', quantity: 4 }
    ],
    result: { itemId: 'spectral_boots', quantity: 1 },
    craftTime: 8,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'spectral_gloves',
    name: 'Spectral Gloves',
    description: 'Gloves that pass through solid matter.',
    ingredients: [
      { itemId: 'ectoplasm', quantity: 2 },
      { itemId: 'fiber', quantity: 3 }
    ],
    result: { itemId: 'spectral_gloves', quantity: 1 },
    craftTime: 6,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'spectral_amulet',
    name: 'Spectral Amulet',
    description: 'An amulet infused with ghostly essence.',
    ingredients: [
      { itemId: 'ectoplasm', quantity: 4 },
      { itemId: 'iron_ore', quantity: 2 }
    ],
    result: { itemId: 'spectral_amulet', quantity: 1 },
    craftTime: 10,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },
  {
    id: 'warriors_ring',
    name: "Warrior's Ring",
    description: 'A ring that empowers combat abilities.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 3 },
      { itemId: 'bone', quantity: 2 },
      { itemId: 'wolf_pelt', quantity: 1 }
    ],
    result: { itemId: 'warriors_ring', quantity: 1 },
    craftTime: 8,
    requiredStation: 'workbench',
    category: ItemType.Armor
  },

  // ==========================================
  // AMMUNITION RECIPES
  // ==========================================

  // Primitive ammo
  {
    id: 'musket_ball',
    name: 'Musket Balls',
    description: 'Lead balls for muskets and flintlock pistols.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 2 },
      { itemId: 'stone', quantity: 1 }
    ],
    result: { itemId: 'musket_ball', quantity: 10 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Resource
  },
  {
    id: 'buckshot',
    name: 'Buckshot',
    description: 'Scattered pellets for the blunderbuss.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 2 },
      { itemId: 'stone', quantity: 2 }
    ],
    result: { itemId: 'buckshot', quantity: 8 },
    craftTime: 3,
    requiredStation: 'workbench',
    category: ItemType.Resource
  },

  // Modern ammo
  {
    id: 'pistol_ammo',
    name: '9mm Rounds',
    description: 'Standard pistol and SMG ammunition.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 3 },
      { itemId: 'stone', quantity: 1 }
    ],
    result: { itemId: 'pistol_ammo', quantity: 15 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Resource
  },
  {
    id: 'rifle_ammo',
    name: 'Rifle Rounds',
    description: 'High-caliber ammunition for rifles.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 4 },
      { itemId: 'stone', quantity: 2 }
    ],
    result: { itemId: 'rifle_ammo', quantity: 10 },
    craftTime: 5,
    requiredStation: 'workbench',
    category: ItemType.Resource
  },
  {
    id: 'shotgun_shells',
    name: 'Shotgun Shells',
    description: 'Shells packed with buckshot.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 3 },
      { itemId: 'stone', quantity: 2 }
    ],
    result: { itemId: 'shotgun_shells', quantity: 8 },
    craftTime: 4,
    requiredStation: 'workbench',
    category: ItemType.Resource
  },

  // Futuristic ammo
  {
    id: 'energy_cell',
    name: 'Energy Cells',
    description: 'Compact energy cells for laser weapons.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 3 },
      { itemId: 'golem_core', quantity: 1 }
    ],
    result: { itemId: 'energy_cell', quantity: 20 },
    craftTime: 6,
    requiredStation: 'workbench',
    category: ItemType.Resource
  },
  {
    id: 'plasma_cell',
    name: 'Plasma Cells',
    description: 'Superheated plasma containers.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 4 },
      { itemId: 'golem_core', quantity: 1 },
      { itemId: 'demon_heart', quantity: 1 }
    ],
    result: { itemId: 'plasma_cell', quantity: 15 },
    craftTime: 8,
    requiredStation: 'workbench',
    category: ItemType.Resource
  },
  {
    id: 'rail_charge',
    name: 'Rail Charges',
    description: 'Electromagnetic accelerator charges.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 5 },
      { itemId: 'golem_core', quantity: 2 },
      { itemId: 'ectoplasm', quantity: 2 }
    ],
    result: { itemId: 'rail_charge', quantity: 5 },
    craftTime: 10,
    requiredStation: 'workbench',
    category: ItemType.Resource
  },

  // ==========================================
  // PRIMITIVE GUNS (Level 10+)
  // ==========================================
  {
    id: 'flintlock_pistol',
    name: 'Flintlock Pistol',
    description: 'A simple black powder pistol.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 8 },
      { itemId: 'wood', quantity: 5 },
      { itemId: 'stone', quantity: 3 }
    ],
    result: { itemId: 'flintlock_pistol', quantity: 1 },
    craftTime: 10,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'musket',
    name: 'Musket',
    description: 'A long-barreled black powder rifle.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 12 },
      { itemId: 'wood', quantity: 8 },
      { itemId: 'stone', quantity: 4 }
    ],
    result: { itemId: 'musket', quantity: 1 },
    craftTime: 15,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'blunderbuss',
    name: 'Blunderbuss',
    description: 'A wide-barreled scatter gun.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 15 },
      { itemId: 'wood', quantity: 6 },
      { itemId: 'stone', quantity: 5 }
    ],
    result: { itemId: 'blunderbuss', quantity: 1 },
    craftTime: 18,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },

  // ==========================================
  // MODERN GUNS (Level 20+)
  // ==========================================
  {
    id: 'pistol_9mm',
    name: '9mm Pistol',
    description: 'A reliable semi-automatic pistol.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 15 },
      { itemId: 'bone', quantity: 5 },
      { itemId: 'wolf_pelt', quantity: 2 }
    ],
    result: { itemId: 'pistol_9mm', quantity: 1 },
    craftTime: 12,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'smg',
    name: 'SMG',
    description: 'A compact submachine gun.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 20 },
      { itemId: 'bone', quantity: 8 },
      { itemId: 'wolf_pelt', quantity: 3 }
    ],
    result: { itemId: 'smg', quantity: 1 },
    craftTime: 18,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'assault_rifle',
    name: 'Assault Rifle',
    description: 'A versatile automatic rifle.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 25 },
      { itemId: 'bone', quantity: 10 },
      { itemId: 'wolf_pelt', quantity: 4 }
    ],
    result: { itemId: 'assault_rifle', quantity: 1 },
    craftTime: 25,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'shotgun_pump',
    name: 'Pump Shotgun',
    description: 'A powerful close-range weapon.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 18 },
      { itemId: 'wood', quantity: 10 },
      { itemId: 'bone', quantity: 6 }
    ],
    result: { itemId: 'shotgun_pump', quantity: 1 },
    craftTime: 20,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'sniper_rifle',
    name: 'Sniper Rifle',
    description: 'A precision long-range rifle.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 30 },
      { itemId: 'bone', quantity: 12 },
      { itemId: 'spider_eye', quantity: 4 }
    ],
    result: { itemId: 'sniper_rifle', quantity: 1 },
    craftTime: 30,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },

  // ==========================================
  // FUTURISTIC GUNS (Level 30+)
  // ==========================================
  {
    id: 'laser_pistol',
    name: 'Laser Pistol',
    description: 'A compact energy sidearm.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 20 },
      { itemId: 'golem_core', quantity: 2 },
      { itemId: 'ectoplasm', quantity: 3 }
    ],
    result: { itemId: 'laser_pistol', quantity: 1 },
    craftTime: 20,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'laser_rifle',
    name: 'Laser Rifle',
    description: 'A precision energy rifle.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 30 },
      { itemId: 'golem_core', quantity: 3 },
      { itemId: 'ectoplasm', quantity: 5 }
    ],
    result: { itemId: 'laser_rifle', quantity: 1 },
    craftTime: 30,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'pulse_smg',
    name: 'Pulse SMG',
    description: 'A rapid-fire energy weapon.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 25 },
      { itemId: 'golem_core', quantity: 2 },
      { itemId: 'ectoplasm', quantity: 4 },
      { itemId: 'bone', quantity: 8 }
    ],
    result: { itemId: 'pulse_smg', quantity: 1 },
    craftTime: 25,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'plasma_rifle',
    name: 'Plasma Rifle',
    description: 'A devastating plasma weapon.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 35 },
      { itemId: 'golem_core', quantity: 4 },
      { itemId: 'demon_heart', quantity: 2 }
    ],
    result: { itemId: 'plasma_rifle', quantity: 1 },
    craftTime: 35,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'plasma_cannon',
    name: 'Plasma Cannon',
    description: 'A heavy plasma launcher.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 50 },
      { itemId: 'golem_core', quantity: 5 },
      { itemId: 'demon_heart', quantity: 3 }
    ],
    result: { itemId: 'plasma_cannon', quantity: 1 },
    craftTime: 45,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  },
  {
    id: 'railgun',
    name: 'Railgun',
    description: 'The ultimate electromagnetic weapon.',
    ingredients: [
      { itemId: 'iron_ore', quantity: 60 },
      { itemId: 'golem_core', quantity: 6 },
      { itemId: 'demon_heart', quantity: 4 },
      { itemId: 'ectoplasm', quantity: 10 }
    ],
    result: { itemId: 'railgun', quantity: 1 },
    craftTime: 60,
    requiredStation: 'workbench',
    category: ItemType.Weapon
  }
];
